/**
 * NATIVE_GAMES — the hardcoded manifest of first-class djgg games that are NOT
 * LLM creations: pong (the reference) + five ported legacy games (IceSpace
 * included, cut over 2026-07-15 — see docs/ICESPACE-PORT.md), each served as a
 * hosted bundle under `/games/<slug>/` (CREATOR-PLATFORM.md hosting topology).
 *
 * These appear as first-class cards in the gallery alongside creations; every
 * card routes same-window to `/g/<slug>`. They carry a `style` so the style
 * filter applies uniformly, and a `link` that is either an internal hosted
 * path (`/games/<slug>/`) or an external URL. The manifest stays the source of
 * card art + PLAY link; the hosted natives additionally have seeded
 * `creations` docs (`kind: 'native'`, id `native-<slug>`, seeded by
 * tools/ops/seed-native-games.mjs) that back the social surface (likes,
 * counters, credits) on the game page.
 */
import type { CreationStyle } from '@djgg/creations';
import { RELEASE_SHA } from '../platform/env.js';

/** Native thumbnail filenames are stable inside one release, so key browser
 * caches by the immutable release identity. A future art replacement gets a
 * new URL automatically even though the human-readable filename stays put. */
export function releaseAssetUrl(path: string): string {
  return `${path}?v=${encodeURIComponent(RELEASE_SHA)}`;
}

/** How a native game is reached from its gallery card / play page. */
export type NativeGameLink =
  | { kind: 'hosted'; path: string }
  | { kind: 'external'; url: string };

/** A hardcoded, non-creation game shown in the gallery. */
export interface NativeGame {
  slug: string;
  title: string;
  description: string;
  style: CreationStyle;
  /** Studio/author label shown where a creation shows its creator handle. */
  author: string;
  link: NativeGameLink;
  /** Cover art (16:9) shown on the card; served from the portal's public/.
   *  Falls back to the deterministic gradient when absent. */
  cover?: string;
  /** Optional static gameplay poster (`/games/<slug>/poster.png`). */
  poster?: string;
  /** Explicit catalog capability used by promotional highlights. */
  multiplayer: boolean;
  /** Optional transparent title treatment; CSS exact-text fallback is used
   * until a native-specific asset is checked in. */
  titleOverlay?: string;
}

/**
 * The native-game manifest. Slugs match the hosting sub-paths the integrator
 * builds (`/games/<slug>/`).
 */
export const NATIVE_GAMES: readonly NativeGame[] = [
  {
    slug: 'pong',
    title: 'Pong',
    description: 'The reference djgg game — deterministic paddle duel vs an adaptive bot or online.',
    style: 'action',
    author: 'jon',
    link: { kind: 'hosted', path: '/games/pong/' },
    cover: releaseAssetUrl('/thumbnails/pong.jpg'),
    poster: '/games/pong/poster.png',
    multiplayer: true,
    titleOverlay: '/title-overlays/pong.png',
  },
  {
    slug: 'tetris',
    title: 'Tetris Revolutions',
    description: 'Stack, clear, and chase the combo. A native djgg port of the block-dropping classic.',
    style: 'puzzle',
    author: 'jon',
    link: { kind: 'hosted', path: '/games/tetris/' },
    cover: releaseAssetUrl('/thumbnails/tetris.jpg'),
    poster: '/games/tetris/poster.png',
    multiplayer: false,
    titleOverlay: '/title-overlays/tetris.png',
  },
  {
    slug: 'starship',
    title: 'Starship Rush',
    description: 'Weave through waves of enemy fire in this fast vertical shooter.',
    style: 'shooter',
    author: 'jon',
    link: { kind: 'hosted', path: '/games/starship/' },
    cover: releaseAssetUrl('/thumbnails/starship.jpg'),
    poster: '/games/starship/poster.png',
    multiplayer: false,
    titleOverlay: '/title-overlays/starship.png',
  },
  {
    slug: 'gemmatch',
    title: 'Gem Match',
    description: 'Swap and cascade sparkling gems to rack up chain reactions.',
    style: 'puzzle',
    author: 'jon',
    link: { kind: 'hosted', path: '/games/gemmatch/' },
    cover: releaseAssetUrl('/thumbnails/gemmatch.jpg'),
    poster: '/games/gemmatch/poster.png',
    multiplayer: false,
    titleOverlay: '/title-overlays/gemmatch.png',
  },
  {
    slug: 'choplifter',
    title: 'Choplifter Rescue',
    description: 'Pilot the chopper, dodge fire, and airlift survivors to safety.',
    style: 'action',
    author: 'jon',
    link: { kind: 'hosted', path: '/games/choplifter/' },
    cover: releaseAssetUrl('/thumbnails/choplifter.jpg'),
    poster: '/games/choplifter/poster.png',
    multiplayer: false,
    titleOverlay: '/title-overlays/choplifter.png',
  },
  {
    slug: 'icespace',
    title: 'IceSpace',
    description: 'The original territory-battle arena game — now hosted natively on djgg.',
    style: 'strategy',
    author: 'jon',
    link: { kind: 'hosted', path: '/games/icespace/' },
    cover: releaseAssetUrl('/thumbnails/icespace.jpg'),
    poster: '/games/icespace/poster.png',
    multiplayer: true,
    titleOverlay: '/title-overlays/icespace.png',
  },
];

/** Look up a native game by slug, or undefined. */
export function nativeGameBySlug(slug: string): NativeGame | undefined {
  return NATIVE_GAMES.find((g) => g.slug === slug);
}
